import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import NotificationStatusChip from '../messages/NotificationStatusChip';
import useExamImport, { importFailureMessage } from './useExamImport';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import UploadIcon from '@mui/icons-material/Upload';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { trTR } from '@mui/x-date-pickers/locales';
import trLocale from 'date-fns/locale/tr';
import { apiUrl, fetchJson } from '../../apiBase';

const importControlSx = {
  minHeight: 44,
  minWidth: 44,
  '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'primary.dark', outlineOffset: 2 },
  '&.Mui-disabled': { color: 'text.disabled', backgroundColor: 'action.disabledBackground', borderColor: 'transparent' },
};

const audienceControlSx = {
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { minWidth: 44, minHeight: 44 },
  '& .MuiChip-root': { minHeight: 44 },
  '& .MuiChip-root .MuiChip-deleteIcon': { width: 44, height: 44, padding: '11px', margin: 0, color: 'text.secondary' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 3 },
};

const fmtDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    // Turkish-friendly long format, per style guide
    return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (_) {
    return d;
  }
};

const toHHMM = (v) => {
  if (!v) return '';
  let s = String(v).trim();
  // Handle 12h with am/pm
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*([ap])\.?m\.?$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const m = m12[2];
    const ap = m12[3].toLowerCase();
    if (ap === 'p' && h !== 12) h += 12;
    if (ap === 'a' && h === 12) h = 0;
    const hh = String(Math.max(0, Math.min(23, h))).padStart(2, '0');
    return `${hh}:${m}`;
  }
  // Handle compact HHMM
  const mCompact = s.match(/^(\d{3,4})$/);
  if (mCompact) {
    const raw = mCompact[1].padStart(4, '0');
    const h = Math.max(0, Math.min(23, parseInt(raw.slice(0, 2), 10)));
    const mm = Math.max(0, Math.min(59, parseInt(raw.slice(2, 4), 10)));
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
  // Handle H:MM or HH:MM (coerce bounds)
  const m24 = s.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m24) {
    const h = Math.max(0, Math.min(23, parseInt(m24[1], 10)));
    const mm = Math.max(0, Math.min(59, parseInt(m24[2], 10)));
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
  // Handle only hour provided (e.g., "9" or "18") → HH:00
  const onlyHour = s.match(/^(\d{1,2})$/);
  if (onlyHour) {
    const h = Math.max(0, Math.min(23, parseInt(onlyHour[1], 10)));
    return `${String(h).padStart(2, '0')}:00`;
  }
  return s;
};

const toPickerDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  const result = new Date(year, month - 1, day);
  return Number.isNaN(result.getTime()) ? null : result;
};

const toIsoDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toPickerTime = (value) => {
  const normalized = toHHMM(value);
  const match = String(normalized || '').match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const result = new Date(2000, 0, 1, Number(match[1]), Number(match[2]), 0, 0);
  return Number.isNaN(result.getTime()) ? null : result;
};

const toTimeString = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
};

const describeAudience = (plan, classOptions) => {
  if (!plan) return 'Hedef kitle seçilmedi';
  if (plan.target_scope === 'classes') {
    const ids = plan.class_ids || [];
    const labels = classOptions.filter((c) => ids.includes(c.id)).map((c) => c.label);
    return labels.length ? `Şubeler: ${labels.join(', ')}` : 'Hedef kitle seçilmedi';
  }
  if (plan.target_scope === 'grade_levels') {
    const grades = plan.grade_levels || [];
    return grades.length ? `Sınıf seviyeleri: ${grades.join(', ')}` : 'Hedef kitle seçilmedi';
  }
  return 'Hedef kitle seçilmedi';
};

export default function MockExamPlanningPage({ schoolId, onError, onSuccess, user }) {
  const canTargetGradeLevels = ['admin', 'superadmin'].includes(String(user?.role || '').trim().toLowerCase());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Guards against stale async responses when user quickly changes selection.
  const selectionTokenRef = useRef(0);

  const [mockExamId, setMockExamId] = useState('');
  const [mockExamDate, setMockExamDate] = useState('');
  const [mockExamStartTime, setMockExamStartTime] = useState('');
  const [mockExamEndTime, setMockExamEndTime] = useState('');
  const [targetScope, setTargetScope] = useState('classes');
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);

  const [mockExamPlans, setMockExamPlans] = useState([]);
  const [selectedMockExamPlan, setSelectedMockExamPlan] = useState(null);
  const [editingMockExamId, setEditingMockExamId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [createdPlan, setCreatedPlan] = useState(null);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [createErrors, setCreateErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const [mockExamPreview, setMockExamPreview] = useState(null);
  const [mockExamMappings, setMockExamMappings] = useState([]);
  const [savingMockExamMapping, setSavingMockExamMapping] = useState(false); // used for per-row apply mapping operations
  const [finalizingMockExam, setFinalizingMockExam] = useState(false);
  const [sendingMockExamMessages, setSendingMockExamMessages] = useState(false);
  const [sendingMockExamPlan, setSendingMockExamPlan] = useState(false);
  const [confirmDeleteMockExamPlan, setConfirmDeleteMockExamPlan] = useState(null); // plan or null

  // Matched results (like MockExamResultsPage view)
  const [loadingMockExamSummary, setLoadingMockExamSummary] = useState(false);
  const [mockExamSummaryRows, setMockExamSummaryRows] = useState([]);

  const resetNotices = useCallback(() => { setError(''); setSuccess(''); onError?.(''); }, [onError]);

  const classOptions = useMemo(() => {
    return (classes || []).map((c) => ({
      ...c,
      id: c.id,
      label: `${c.name || c.code || ''}`.trim() || String(c.id),
      grade_level: String(c.grade_level || '').trim(),
    }));
  }, [classes]);

  const gradeOptions = useMemo(() => {
    const seen = new Set();
    (classes || []).forEach((c) => {
      const grade = String(c.grade_level || '').trim();
      if (grade) seen.add(grade);
    });
    return Array.from(seen).sort((a, b) => String(a).localeCompare(String(b), 'tr-TR')).map((grade) => ({ key: grade, label: `${grade}. Sınıf` }));
  }, [classes]);

  const selectedAudienceSummary = useMemo(() => {
    return describeAudience(selectedMockExamPlan, classOptions);
  }, [selectedMockExamPlan, classOptions]);

  const audienceScope = canTargetGradeLevels ? targetScope : 'classes';
  const hasSelectedAudience = (audienceScope === 'classes' && selectedClasses.length > 0) || (audienceScope === 'grade_levels' && selectedGrades.length > 0);

  const loadMockExamPlans = useCallback(async () => {
    resetNotices();
    setLoading(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}`));
      const data = await res.json();
      setMockExamPlans(data?.items || []);
    } catch (e) {
      setError('Planlar yüklenemedi.');
      onError?.('Planlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [resetNotices, schoolId, onError]);

  useEffect(() => { if (schoolId) loadMockExamPlans(); }, [schoolId, loadMockExamPlans]);

  useEffect(() => {
    let active = true;
    const loadClasses = async () => {
      if (!schoolId) return;
      try {
        const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
        if (!res.ok) throw new Error('load_classes');
        const data = await res.json();
        if (active) setClasses(Array.isArray(data) ? data : []);
      } catch (_) {
        if (active) setClasses([]);
      }
    };
    void loadClasses();
    return () => { active = false; };
  }, [schoolId]);

  useEffect(() => {
    if ((!showCreate && !showEdit) || canTargetGradeLevels || audienceScope !== 'classes') return;
    if (selectedClasses.length > 0) return;
    if (classOptions.length !== 1) return;
    setSelectedClasses([classOptions[0]]);
  }, [showCreate, showEdit, canTargetGradeLevels, audienceScope, selectedClasses.length, classOptions]);

  const resetPlanForm = useCallback(() => {
    setEditingMockExamId('');
    setMockExamId('');
    setMockExamDate('');
    setMockExamStartTime('');
    setMockExamEndTime('');
    setTargetScope('classes');
    setSelectedClasses([]);
    setSelectedGrades([]);
    setReminderEnabled(true);
    setCreatedPlan(null);
    setCreateErrors({});
    setEditErrors({});
  }, []);

  const handleOpenCreateMockExamDialog = useCallback(() => {
    resetNotices();
    resetPlanForm();
    setShowCreate(true);
  }, [resetNotices, resetPlanForm]);

  const handleCloseCreate = useCallback(() => {
    setShowCreate(false);
    resetPlanForm();
  }, [resetPlanForm]);

  const handleCloseEdit = useCallback(() => {
    setShowEdit(false);
    resetPlanForm();
  }, [resetPlanForm]);

  const handleCreateMockExamPlan = async () => {
    resetNotices();
    const nextErrors = {};
    if (!mockExamId?.trim()) {
      nextErrors.exam_id = 'Sınav kodu gerekli';
    }
    if (!String(mockExamDate || '').trim()) {
      nextErrors.exam_date = 'Tarih gerekli';
    }
    if (!canTargetGradeLevels && selectedClasses.length === 0) {
      nextErrors.class_ids = 'En az bir şube seçin.';
    }
    setCreateErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstMessage = Object.values(nextErrors)[0];
      setError(firstMessage);
      onError?.(firstMessage);
      return;
    }
    setCreating(true);
    try {
      const body = {
        exam_id: mockExamId.trim(),
        title: mockExamId.trim(),
        exam_date: mockExamDate,
        start_time: mockExamStartTime,
        end_time: mockExamEndTime,
        status: 'planned',
        reminder_enabled: reminderEnabled,
        target_scope: hasSelectedAudience ? audienceScope : null,
        class_ids: audienceScope === 'classes' ? selectedClasses.map((c) => c.id) : [],
        grade_levels: audienceScope === 'grade_levels' ? selectedGrades.map((g) => g.key) : [],
      };
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error('save_fail');
      const item = data?.item || data?.saved || null;
      setCreatedPlan(item);
      if (item) setSelectedMockExamPlan(item);
      setSuccess('Plan kaydedildi. İsterseniz şimdi plan mesajını gönderebilirsiniz.');
      onSuccess?.('Plan kaydedildi.');
      await loadMockExamPlans();
    } catch (_) {
      setError('Plan kaydedilemedi.');
    } finally {
      setCreating(false);
    }
  };

  const handleStartEditMockExamPlan = (p) => {
    resetNotices();
    setEditingMockExamId(p.exam_id);
    setMockExamId(p.exam_id || '');
    setMockExamDate(p.exam_date || '');
    setMockExamStartTime(toHHMM(p.start_time || ''));
    setMockExamEndTime(toHHMM(p.end_time || ''));
    setTargetScope(canTargetGradeLevels ? (p.target_scope || 'classes') : 'classes');
    setEditErrors({});
    setReminderEnabled(p.reminder_enabled !== false);
    const rowClassIds = (p.class_ids || []).map((x) => String(x));
    setSelectedClasses(classOptions.filter((c) => rowClassIds.includes(String(c.id))));
    const rowGrades = (p.grade_levels || []).map((x) => String(x));
    setSelectedGrades(gradeOptions.filter((g) => rowGrades.includes(String(g.key))));
    setShowEdit(true);
  };

  const handleUpdateMockExamPlan = async () => {
    resetNotices();
    if (!editingMockExamId) return;
    const nextErrors = {};
    if (!String(mockExamDate || '').trim()) {
      nextErrors.exam_date = 'Tarih gerekli';
    }
    if (!canTargetGradeLevels && selectedClasses.length === 0) {
      nextErrors.class_ids = 'En az bir şube seçin.';
    }
    setEditErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstMessage = Object.values(nextErrors)[0];
      setError(firstMessage);
      onError?.(firstMessage);
      return;
    }
    setEditing(true);
    try {
      const body = {
        exam_date: mockExamDate,
        start_time: mockExamStartTime,
        end_time: mockExamEndTime,
        reminder_enabled: reminderEnabled,
        target_scope: hasSelectedAudience ? audienceScope : null,
        class_ids: audienceScope === 'classes' ? selectedClasses.map((c) => c.id) : [],
        grade_levels: audienceScope === 'grade_levels' ? selectedGrades.map((g) => g.key) : [],
      };
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(editingMockExamId)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('update_failed');
      setSuccess('Plan güncellendi.');
      onSuccess?.('Plan güncellendi.');
      await loadMockExamPlans();
      handleCloseEdit();
    } catch (_) {
      setError('Plan güncellenemedi.');
    } finally {
      setEditing(false);
    }
  };

  const performDeleteMockExamPlan = async (p) => {
    resetNotices();
    setLoading(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(p.exam_id)}`), {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('delete_failed');
      setSuccess('Plan silindi.');
      if (selectedMockExamPlan?.exam_id === p.exam_id) setSelectedMockExamPlan(null);
      if (editingMockExamId === p.exam_id) handleCloseEdit();
      await loadMockExamPlans();
    } catch (_) {
      setError('Plan silinemedi.');
    } finally {
      setLoading(false);
    }
  };

  const loadMockExamPreview = useCallback(async (mockExamIdForPreview, token) => {
    if (!schoolId || !mockExamIdForPreview) return;
    const isCurrent = () => (!token || token === selectionTokenRef.current);
    if (!isCurrent()) return;

    setLoadingMockExamSummary(true);

    try {
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(mockExamIdForPreview)}/preview`));
      if (!res.ok) throw new Error('preview_failed');
      const data = await res.json();
      if (!isCurrent()) return;
      setMockExamPreview(data);

      // Use preview's matched items as the table source to avoid extra /summary call.
      const items = (data?.items || []).map((r) => {
        const fullName = (r.full_name || `${r.name || ''} ${r.surname || ''}`).trim();
        const schoolRank = (r.school_rank ?? r.rank_school ?? r.rankSchool ?? null);
        const classRank = (r.class_rank ?? r.rank_class ?? r.rankClass ?? null);
        const overallRank = (r.general_rank ?? r.rank_overall ?? r.overall_rank ?? null);
        const totalScore = r.total_score ?? r.totalScore ?? null;
        const lgsScore = r.lgs_score ?? r.lgsScore ?? totalScore;
        return {
          ...r,
          full_name: fullName,
          school_rank: schoolRank,
          class_rank: classRank,
          general_rank: overallRank,
          overall_rank: r.overall_rank ?? overallRank,
          lgs_score: lgsScore,
        };
      });

      const toNum = (v) => (typeof v === 'number' ? v : (v == null ? NaN : Number(String(v).replace(',', '.'))));
      const rankVal = (x) => {
        const rv = toNum(x.school_rank);
        return Number.isFinite(rv) ? rv : Number.MAX_SAFE_INTEGER;
      };
      const netVal = (x) => {
        const nv = toNum(x.total_net);
        return Number.isFinite(nv) ? nv : -1e9;
      };
      items.sort((a, b) => (
        (rankVal(a) - rankVal(b)) ||
        (netVal(b) - netVal(a)) ||
        (a.full_name || '').localeCompare(b.full_name || '', 'tr-TR')
      ));

      setMockExamSummaryRows(items);

      const unr = data?.unregistered || [];
      setMockExamMappings(unr.map((u) => ({ full_name: u.full_name || '', class_name: u.class_name || '', student_no: u.student_no || '', student_id: '' })));
    } catch (_) {
      if (!isCurrent()) return;
      setMockExamPreview(null);
      setMockExamMappings([]);
      setMockExamSummaryRows([]);
    }
    finally {
      if (isCurrent()) setLoadingMockExamSummary(false);
    }
  }, [schoolId]);

  const importJob = useExamImport(schoolId, selectedMockExamPlan?.exam_id, (result) => {
    if (!selectedMockExamPlan || !result) return;
    setFile(null);
    const token = ++selectionTokenRef.current;
    void loadMockExamPreview(selectedMockExamPlan.exam_id, token);
    void loadMockExamPlans();
    // Plan refresh clears older notices synchronously; publish this completion
    // afterwards so the confirmed import result remains visible.
    setSuccess(`Yüklendi. Eşleşen: ${result.matched_count}, Kayıtsız: ${result.unregistered_count}`);
  });
  const importBusy = uploading || importJob.busy;

  useEffect(() => {
    selectionTokenRef.current += 1;
    setSelectedMockExamPlan(null);
    setMockExamPreview(null);
    setFile(null);
  }, [schoolId]);

  const handleSelectMockExamPlan = (p) => {
    resetNotices();
    setSelectedMockExamPlan(p);
    setMockExamPreview(null);
    setMockExamMappings([]);
    setFile(null);
    setMockExamSummaryRows([]);
    if (p) {
      const token = ++selectionTokenRef.current;
      // Preview already contains matched+unregistered data, so a single BE call is enough.
      void loadMockExamPreview(p.exam_id, token);
    }
  };

  const handleUploadMockExamFile = async () => {
    resetNotices();
    if (!selectedMockExamPlan) { setError('Önce bir plan seçin.'); return; }
    if (!file) { setError('Yüklenecek dosyayı seçin.'); return; }
    if (importBusy) return;
    if (file.size > 20 * 1024 * 1024) { setError('Dosya 20 MB sınırını aşıyor.'); return; }
    const token = selectionTokenRef.current;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (selectedMockExamPlan.exam_date) fd.append('exam_date_iso', selectedMockExamPlan.exam_date);
      const res = await fetch(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(selectedMockExamPlan.exam_id)}/upload`), {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        const failure = new Error(data?.detail || 'Dosya işleme alınamadı.');
        failure.confirmed = true;
        throw failure;
      }
      if (token === selectionTokenRef.current) importJob.refresh();
    } catch (failure) {
      if (token !== selectionTokenRef.current) return;
      setError(failure.confirmed ? failure.message : 'Yükleme yanıtı alınamadı. İşlem durumu kontrol ediliyor; dosyayı hemen tekrar göndermeyin.');
      importJob.refresh();
    } finally {
      setUploading(false);
    }
  };

  const handleFinalizeMockExamPlan = async () => {
    resetNotices();
    if (!selectedMockExamPlan) return;
    setFinalizingMockExam(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(selectedMockExamPlan.exam_id)}/finalize`), {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) throw new Error('finalize_failed');
      setSuccess(`Kaydedildi. Taşınan: ${data.moved_rows}`);
      // Optimistically mark finalized so button swaps immediately
      setSelectedMockExamPlan(sp => sp ? { ...sp, status: 'finalized' } : sp);
      // Refresh plans + preview to reflect any moved rows
      await loadMockExamPlans();
      const token = ++selectionTokenRef.current;
      void loadMockExamPreview(selectedMockExamPlan.exam_id, token);
    } catch (_) {
      setError('Tamamlama başarısız.');
    } finally {
      setFinalizingMockExam(false);
    }
  };

  const handleSendMockExamMessages = async () => {
    resetNotices();
    if (!selectedMockExamPlan) return;
    setSendingMockExamMessages(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(selectedMockExamPlan.exam_id)}/send-messages`), {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) throw new Error('send_failed');
      setSuccess(`Kuyruğa eklendi: ${data.queued || 0} mesaj. Hata: ${data.failed || 0}`);
      handleSelectMockExamPlan(selectedMockExamPlan);
      await loadMockExamPlans();
    } catch (_) {
      setError('Mesaj gönderilemedi.');
    } finally {
      setSendingMockExamMessages(false);
    }
  };

  const handleSendMockExamPlan = async () => {
    resetNotices();
    if (!selectedMockExamPlan) return;
    return handleSendMockExamPlanFor(selectedMockExamPlan);
  };

  const handleSendMockExamPlanFor = async (plan) => {
    resetNotices();
    if (!plan) return;
    setSendingMockExamPlan(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(plan.exam_id)}/send-plan`), {
        method: 'POST',
      });
      const data = await res.json();
      if (!res.ok) throw new Error('send_plan_failed');
      setSuccess(`Plan bildirimleri kuyruğa eklendi: ${data.queued || 0} mesaj`);
      await loadMockExamPlans();
      const token = ++selectionTokenRef.current;
      void loadMockExamPreview(plan.exam_id, token);
    } catch (_) {
      setError('Plan mesajı gönderilemedi.');
    } finally {
      setSendingMockExamPlan(false);
    }
  };

  const handleSendCreatedPlan = async () => {
    if (!createdPlan) return;
    await handleSendMockExamPlanFor(createdPlan);
    handleCloseCreate();
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={trLocale}
      localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>Planlar</Typography>
                <Typography variant="body2" color="text.secondary">Yeni plan ekleme ve mevcut plan düzenleme işlemleri modal üzerinden yapılır.</Typography>
              </Box>
              <Button variant="contained" onClick={handleOpenCreateMockExamDialog}>Yeni Sınav</Button>
            </Box>
            <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {mockExamPlans.length === 0 && (
                <Typography variant="body2" color="text.secondary">Henüz kayıtlı plan yok.</Typography>
              )}
              {mockExamPlans.map((p) => {
                const statusMap = { planned: 'Planlandı', finalized: 'Tamamlandı' };
                const statusLabel = statusMap[p.status] || p.status;
                const selected = selectedMockExamPlan?.exam_id === p.exam_id;
                return (
                  <Box
                    key={p.exam_id}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 1,
                      borderRadius: 2,
                      mb: 0.5,
                      transition: 'background .15s',
                      backgroundColor: selected ? 'action.selected' : 'transparent',
                      '&:hover': { backgroundColor: selected ? 'action.selected' : 'action.hover' },
                    }}
                  >
                    <Box sx={{ minWidth: 0, mr: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{p.exam_id}</Typography>
                        <Chip size="small" label={statusLabel} color={p.status === 'finalized' ? 'success' : 'primary'} variant={p.status === 'finalized' ? 'filled' : 'outlined'} sx={{ height: 20 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {fmtDate(p.exam_date)} • {toHHMM(p.start_time) || '-'} – {toHHMM(p.end_time) || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {p.target_scope === 'classes'
                          ? `Şubeler: ${(p.class_ids || []).length || 0}`
                          : p.target_scope === 'grade_levels'
                            ? `Seviyeler: ${(p.grade_levels || []).join(', ') || '-'}`
                            : 'Hedef kitle seçilmedi'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <Tooltip title="Planı seç">
                        <Button size="small" sx={importControlSx} disabled={uploading} variant={selected ? 'contained' : 'outlined'} onClick={() => handleSelectMockExamPlan(p)}>{selected ? 'Seçildi' : 'Seç'}</Button>
                      </Tooltip>
                      <Tooltip title="Planı düzenle">
                        <Button size="small" sx={importControlSx} disabled={selected && importBusy} variant="text" onClick={() => handleStartEditMockExamPlan(p)}>Düzenle</Button>
                      </Tooltip>
                      <Tooltip title="Planı sil">
                        <Button size="small" sx={importControlSx} disabled={selected && importBusy} variant="text" color="error" onClick={() => setConfirmDeleteMockExamPlan(p)}>Sil</Button>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Upload & Preview */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Yükle ve Önizle</Typography>
            {!selectedMockExamPlan && <Alert severity="info">Önce bir plan seçin.</Alert>}
            {selectedMockExamPlan && (
              <>
                {importBusy && (
                  <Alert severity="info" role="status" sx={{ mb: 2 }}>
                    {uploading ? 'Dosya yükleniyor…' : importJob.connectionLost
                      ? 'İşlem durumu alınamıyor. Bağlantı yeniden kontrol ediliyor; dosyayı tekrar göndermeyin.'
                      : importJob.status === 'checking' ? 'Dosya işleme durumu kontrol ediliyor…'
                        : 'Dosya işleniyor. Bu sırada diğer ekranları kullanabilirsiniz; bu planı yeniden seçtiğinizde işlem durumu gösterilir.'}
                    <LinearProgress aria-label="Dosya işleme" sx={{ mt: 1 }} />
                  </Alert>
                )}
                {importJob.status === 'failed' && <Alert severity="error" sx={{ mb: 2 }}>{importFailureMessage(importJob.error_code)}</Alert>}
                {importJob.status === 'succeeded' && importJob.result?.duplicates_skipped > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Tekrarlanan {importJob.result.duplicates_skipped} öğrenci kaydı atlandı.
                    {(importJob.result.duplicate_students || []).map((student) => student.full_name).filter(Boolean).join(', ')}
                  </Alert>
                )}
                <Alert severity={selectedMockExamPlan.target_scope ? 'info' : 'warning'} sx={{ mb: 2 }}>
                  {selectedAudienceSummary}
                  {selectedMockExamPlan.plan_message_last_sent_at ? ` • Son plan gönderimi: ${fmtDate(selectedMockExamPlan.plan_message_last_sent_at)}` : ''}
                </Alert>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    disabled={importBusy}
                    sx={importControlSx}
                  >
                    Dosya Seç
                  </Button>
                  <input
                    hidden
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    aria-label="Sınav sonuç dosyası"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }}
                  />
                  <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'} sx={{ mr: 1 }}>
                    {file ? file.name : 'Excel/PDF dosyası seçin'}
                  </Typography>
                  {file && (
                    <Tooltip title="Temizle">
                      <Button size="small" disabled={importBusy} sx={importControlSx} variant="text" color="inherit" onClick={() => setFile(null)} aria-label="Seçimi temizle" startIcon={<ClearIcon fontSize="small" />}>Temizle</Button>
                    </Tooltip>
                  )}
                  <Button disabled={importBusy || !file} sx={importControlSx} variant="contained" onClick={handleUploadMockExamFile}>{importBusy ? 'İşleniyor…' : 'Yükle ve Ayrıştır'}</Button>
                </Box>
                <Typography variant="caption" color="text.secondary">PDF veya Excel dosyaları desteklenir. Dosya boyutu en fazla 20 MB olabilir.</Typography>
                <Divider sx={{ my: 2 }} />
                {!importBusy && mockExamPreview ? (
                  <Box>
                    <Typography variant="body2">Eşleşen: {mockExamPreview.matched_count} • Kayıtsız: {mockExamPreview.unregistered_count}</Typography>
                    {/* Matched results table */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Eşleşen Sonuçlar (önizleme)</Typography>
                      {loadingMockExamSummary ? (
                        <LinearProgress sx={{ my: 1 }} />
                      ) : (
                        mockExamSummaryRows.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">Eşleşen sonuç görünmüyor.</Typography>
                        ) : (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Ad Soyad</TableCell>
                                  <TableCell>Şube</TableCell>
                                  <TableCell align="right">Toplam Net</TableCell>
                                  <TableCell align="right">Doğru</TableCell>
                                  <TableCell align="right">Yanlış</TableCell>
                                  <TableCell align="right">Boş</TableCell>
                                  <TableCell align="right">Toplam Puan</TableCell>
                                  <TableCell align="right">Okul Sırası</TableCell>
                                  <TableCell align="right">Mesaj</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {mockExamSummaryRows.map((r) => (
                                  <TableRow key={r.student_id || r.full_name}>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.full_name || '-'}</Typography>
                                      <Typography variant="caption" color="text.secondary">#{r.student_id || ''}</Typography>
                                    </TableCell>
                                    <TableCell>{r.class_name || '-'}</TableCell>
                                    <TableCell align="right">{typeof r.total_net === 'number' ? r.total_net.toFixed(2) : '-'}</TableCell>
                                    <TableCell align="right">{r.total_correct ?? '-'}</TableCell>
                                    <TableCell align="right">{r.total_wrong ?? '-'}</TableCell>
                                    <TableCell align="right">{r.total_blank ?? '-'}</TableCell>
                                    <TableCell align="right">{typeof (r.total_score ?? r.lgs_score) === 'number' ? (r.total_score ?? r.lgs_score).toFixed(2) : '-'}</TableCell>
                                    <TableCell align="right">{r.school_rank ?? '-'}</TableCell>
                                    <TableCell align="right"><NotificationStatusChip status={r.notification_status || (r.message_sent ? 'sent' : null)} /></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">Önizleme yok.</Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Mapping */}
        {selectedMockExamPlan && mockExamPreview && (mockExamPreview.unregistered_count > 0) && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Sınav Eşleştirme</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ad Soyad</TableCell>
                      <TableCell>Şube</TableCell>
                      <TableCell>Okul No</TableCell>
                      <TableCell>Öğrenci ID</TableCell>
                      <TableCell>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockExamMappings.map((m, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{m.full_name}</TableCell>
                        <TableCell>{m.class_name}</TableCell>
                        <TableCell>{m.student_no}</TableCell>
                        <TableCell>
                          <TextField size="small" placeholder="örn. ST011" value={m.student_id}
                            inputProps={{ 'aria-label': `${m.full_name} için öğrenci ID` }}
                            sx={{ '& .MuiInputBase-root': { minHeight: 44 } }}
                            disabled={importBusy || selectedMockExamPlan?.status === 'finalized'}
                            onChange={(e) => {
                              const v = e.target.value;
                              setMockExamMappings((prev) => prev.map((x, i) => i === idx ? { ...x, student_id: v } : x));
                            }} />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            sx={importControlSx}
                            variant="outlined"
                            disabled={importBusy || !m.student_id || savingMockExamMapping || selectedMockExamPlan?.status === 'finalized'}
                            onClick={async () => {
                              if (!m.student_id || selectedMockExamPlan?.status === 'finalized') return;
                              try {
                                setSavingMockExamMapping(true);
                                const body = { full_name: m.full_name, class_name: m.class_name, student_no: m.student_no, student_id: m.student_id };
                                const res = await fetchJson(apiUrl(`/admin/exam-plans/${encodeURIComponent(schoolId)}/${encodeURIComponent(selectedMockExamPlan.exam_id)}/apply-mapping`), {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(body),
                                });
                                await res.json();
                                setMockExamMappings((prev) => prev.filter((_, i) => i !== idx));
                                handleSelectMockExamPlan(selectedMockExamPlan);
                                setSuccess('Eşleştirme uygulandı.');
                              } catch (_) {
                                setError('Eşleştirme başarısız.');
                              } finally {
                                setSavingMockExamMapping(false);
                              }
                            }}
                          >Eşle</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {selectedMockExamPlan && mockExamPreview && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Button
                variant="outlined"
                sx={importControlSx}
                disabled={sendingMockExamPlan || !selectedMockExamPlan.target_scope}
                onClick={handleSendMockExamPlan}
              >
                {sendingMockExamPlan ? 'Plan Gönderiliyor…' : 'Plan Mesajını Gönder'}
              </Button>
              {selectedMockExamPlan?.status === 'finalized' ? (
                <Button variant="contained" color="success" sx={importControlSx} disabled={importBusy || sendingMockExamMessages} onClick={handleSendMockExamMessages}>{sendingMockExamMessages ? 'Gönderiliyor…' : 'Whatsapp Mesajları Gönder'}</Button>
              ) : (
                <Button variant="contained" color="success" sx={importControlSx} disabled={importBusy || finalizingMockExam} onClick={handleFinalizeMockExamPlan}>{finalizingMockExam ? 'Kaydediliyor…' : 'Kaydet'}</Button>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(confirmDeleteMockExamPlan)} onClose={() => setConfirmDeleteMockExamPlan(null)} aria-labelledby="delete-title" aria-describedby="delete-desc">
        <DialogTitle id="delete-title">Planı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-desc">
            {confirmDeleteMockExamPlan ? `${confirmDeleteMockExamPlan.exam_id} planı silinsin mi? Bu işlem geri alınamaz.` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteMockExamPlan(null)}>İptal</Button>
          <Button color="error" variant="contained" onClick={async () => { const p = confirmDeleteMockExamPlan; setConfirmDeleteMockExamPlan(null); if (p) await performDeleteMockExamPlan(p); }} autoFocus>Sil</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showCreate} onClose={handleCloseCreate} fullWidth maxWidth="sm">
        <DialogTitle>{createdPlan ? '2. Adım: Planı Gönder' : 'Yeni Sınav'}</DialogTitle>
        <DialogContent>
          {createdPlan ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="success" variant="outlined">
                Sınav oluşturuldu. Şimdi plan mesajını velilere gönderme adımındasınız.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" fontWeight={600}>{createdPlan.exam_id}</Typography>
                  <Typography variant="body2" color="text.secondary">Tarih: {createdPlan.exam_date ? fmtDate(createdPlan.exam_date) : '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">Saat: {toHHMM(createdPlan.start_time) || '-'} - {toHHMM(createdPlan.end_time) || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">Kapsam: {describeAudience(createdPlan, classOptions)}</Typography>
                  <Typography variant="body2" color="text.secondary">Hatırlatma: {createdPlan.reminder_enabled ? 'Açık' : 'Kapalı'}</Typography>
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {error ? <Alert severity="error" variant="outlined">{error}</Alert> : null}
              <TextField
                fullWidth
                label="Sınav Kodu"
                value={mockExamId}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setMockExamId(nextValue);
                  setCreateErrors((prev) => ({ ...prev, exam_id: nextValue.trim() ? null : prev.exam_id }));
                }}
                error={Boolean(createErrors.exam_id)}
                helperText={createErrors.exam_id || ''}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
                <DatePicker
                  label="Tarih"
                  value={toPickerDate(mockExamDate)}
                  onChange={(value) => {
                    const nextValue = toIsoDate(value);
                    setMockExamDate(nextValue);
                    setCreateErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                  }}
                  format="dd.MM.yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(createErrors.exam_date),
                      helperText: createErrors.exam_date || '',
                      sx: { minWidth: 160 },
                    },
                  }}
                />
                <TimePicker
                  label="Başlangıç"
                  ampm={false}
                  value={toPickerTime(mockExamStartTime)}
                  onChange={(value) => setMockExamStartTime(toTimeString(value))}
                  format="HH:mm"
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <TimePicker
                  label="Bitiş"
                  ampm={false}
                  value={toPickerTime(mockExamEndTime)}
                  onChange={(value) => setMockExamEndTime(toTimeString(value))}
                  format="HH:mm"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
              {canTargetGradeLevels && <TextField
                select
                fullWidth
                id="create-mock-exam-audience-scope"
                label="Hedef Kitle Türü"
                value={targetScope}
                onChange={(e) => {
                  const next = e.target.value;
                  setTargetScope(next);
                  setSelectedClasses([]);
                  setSelectedGrades([]);
                }}
                SelectProps={{ native: true }}
              >
                <option value="classes">Şubelere göre</option>
                <option value="grade_levels">Sınıf seviyesine göre</option>
              </TextField>}
              {audienceScope === 'classes' ? (
                <Autocomplete
                  id="create-mock-exam-classes"
                  sx={audienceControlSx}
                  clearText="Temizle"
                  openText="Şubeleri aç"
                  closeText="Şubeleri kapat"
                  multiple
                  options={classOptions}
                  value={selectedClasses}
                  onChange={(_, value) => {
                    resetNotices();
                    setSelectedClasses(value || []);
                    setCreateErrors((prev) => ({ ...prev, class_ids: null }));
                  }}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  getOptionLabel={(option) => option.label || String(option.id)}
                  renderInput={(params) => <TextField {...params} label="Şubeler" required={!canTargetGradeLevels} error={Boolean(createErrors.class_ids)} helperText={createErrors.class_ids || (canTargetGradeLevels ? 'Boş bırakılırsa plan mesajı ve hatırlatma gönderilemez.' : 'Yalnızca yetkili olduğunuz şubeler için deneme planlayabilirsiniz.')} />}
                />
              ) : (
                <Autocomplete
                  sx={audienceControlSx}
                  multiple
                  options={gradeOptions}
                  value={selectedGrades}
                  onChange={(_, value) => setSelectedGrades(value || [])}
                  isOptionEqualToValue={(option, value) => String(option.key) === String(value.key)}
                  getOptionLabel={(option) => option.label || String(option.key)}
                  renderInput={(params) => <TextField {...params} label="Sınıf Seviyeleri" helperText="Boş bırakılırsa plan kaydedilir ama plan/reminder gönderimi hedef bulamaz." />}
                />
              )}
              <FormControlLabel
                control={<Switch checked={!!reminderEnabled} onChange={(e) => setReminderEnabled(e.target.checked)} />}
                label="1 gün önce hatırlatma mesajı gönder"
              />
              <Alert severity="info" variant="outlined">
                İlk adımda sınav oluşturulur. Oluşturma sonrası otomatik olarak plan gönderme adımına geçersiniz.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {createdPlan ? (
            <>
              <Button onClick={handleCloseCreate} disabled={sendingMockExamPlan}>Atla</Button>
              <Button variant="contained" color="success" onClick={handleSendCreatedPlan} disabled={sendingMockExamPlan}>Planı Gönder</Button>
            </>
          ) : (
            <>
              <Button sx={importControlSx} onClick={handleCloseCreate} disabled={creating}>Vazgeç</Button>
              <Button sx={importControlSx} variant="contained" onClick={handleCreateMockExamPlan} disabled={creating}>Oluştur</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={showEdit} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Sınavı Düzenle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error ? <Alert severity="error" variant="outlined">{error}</Alert> : null}
            <TextField
              fullWidth
              label="Sınav Kodu"
              value={mockExamId}
              disabled
              helperText="Sınav kodu düzenleme sırasında sabittir."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
              <DatePicker
                label="Tarih"
                value={toPickerDate(mockExamDate)}
                onChange={(value) => {
                  const nextValue = toIsoDate(value);
                  setMockExamDate(nextValue);
                  setEditErrors((prev) => ({ ...prev, exam_date: nextValue ? null : prev.exam_date }));
                }}
                format="dd.MM.yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: Boolean(editErrors.exam_date),
                    helperText: editErrors.exam_date || '',
                    sx: { minWidth: 160 },
                  },
                }}
              />
              <TimePicker
                label="Başlangıç"
                ampm={false}
                value={toPickerTime(mockExamStartTime)}
                onChange={(value) => setMockExamStartTime(toTimeString(value))}
                format="HH:mm"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TimePicker
                label="Bitiş"
                ampm={false}
                value={toPickerTime(mockExamEndTime)}
                onChange={(value) => setMockExamEndTime(toTimeString(value))}
                format="HH:mm"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
            {canTargetGradeLevels && <TextField
              select
              fullWidth
              id="edit-mock-exam-audience-scope"
              label="Hedef Kitle Türü"
              value={targetScope}
              onChange={(e) => {
                const next = e.target.value;
                setTargetScope(next);
                setSelectedClasses([]);
                setSelectedGrades([]);
              }}
              SelectProps={{ native: true }}
            >
              <option value="classes">Şubelere göre</option>
              <option value="grade_levels">Sınıf seviyesine göre</option>
            </TextField>}
            {audienceScope === 'classes' ? (
              <Autocomplete
                id="edit-mock-exam-classes"
                sx={audienceControlSx}
                clearText="Temizle"
                openText="Şubeleri aç"
                closeText="Şubeleri kapat"
                multiple
                options={classOptions}
                value={selectedClasses}
                onChange={(_, value) => {
                  resetNotices();
                  setSelectedClasses(value || []);
                  setEditErrors((prev) => ({ ...prev, class_ids: null }));
                }}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                getOptionLabel={(option) => option.label || String(option.id)}
                renderInput={(params) => <TextField {...params} label="Şubeler" required={!canTargetGradeLevels} error={Boolean(editErrors.class_ids)} helperText={editErrors.class_ids || (canTargetGradeLevels ? 'Boş bırakılırsa plan mesajı ve hatırlatma gönderilemez.' : 'Yalnızca yetkili olduğunuz şubeler için deneme planlayabilirsiniz.')} />}
              />
            ) : (
              <Autocomplete
                sx={audienceControlSx}
                multiple
                options={gradeOptions}
                value={selectedGrades}
                onChange={(_, value) => setSelectedGrades(value || [])}
                isOptionEqualToValue={(option, value) => String(option.key) === String(value.key)}
                getOptionLabel={(option) => option.label || String(option.key)}
                renderInput={(params) => <TextField {...params} label="Sınıf Seviyeleri" helperText="Boş bırakılırsa plan kaydedilir ama plan/reminder gönderimi hedef bulamaz." />}
              />
            )}
            <FormControlLabel
              control={<Switch checked={!!reminderEnabled} onChange={(e) => setReminderEnabled(e.target.checked)} />}
              label="1 gün önce hatırlatma mesajı gönder"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button sx={importControlSx} onClick={handleCloseEdit} disabled={editing}>Vazgeç</Button>
          <Button sx={importControlSx} variant="contained" onClick={handleUpdateMockExamPlan} disabled={editing}>Güncelle</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </LocalizationProvider>
  );
}
